import { AfterViewInit, Component, ElementRef, Inject, ViewChild } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { FeatureFlagDirective } from '../../../../feature-flags/feature-flag.directive';
import { CompactNumberValuesPipe } from '../../../../kyloe-pipes.pipe';
import { RippleDirective } from '../../../../ripple.directive';
import { UtilsService } from '../../../../shared/services/utils.service';
import { ActionsOperators, AllPrimaryEntitiesNames, IDataRule, RuleData } from '../../../data-rules-model';
import { ISavedDataRuleEventData } from '../../../data-rules.component';
import { UpdateLimitPopupComponent } from '../../../lib/update-limit-popup/update-limit-popup.component';
import { RuleFoldersComponent } from '../rule-folders/rule.component';
import { InlineEditorComponent } from '../../../../shared/inline-editor/inline-editor.component';
import { ENRICH_CREDIT_EXHAUSTED_TOOLTIP, EnrichmentCreditService } from '../../../../enrichment-settings/enrichment-credit.service';

export enum RuleModalCloseAction {
  Duplicate = 'duplicate',
  Delete = 'delete'
}

export interface IRuleModalData {
  dataRule: IDataRule;
  ruleIndex: number;
  currentRuleExamples: RuleData;
  selectedEntity: AllPrimaryEntitiesNames;
  tasksRunning: any[];
  entityMap: any;
  savedRuleEvent: Subject<ISavedDataRuleEventData>;
  onArchiveRequested?: () => void;
  onUnarchiveRequested?: () => void;
}

@Component({
  selector: 'app-rule-modal',
  templateUrl: './rule-modal.component.html',
  styleUrls: ['./rule-modal.component.scss'],
  imports: [
    CompactNumberValuesPipe,
    FeatureFlagDirective,
    MatDialogContent,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    RuleFoldersComponent,
    RippleDirective,
    ReactiveFormsModule,
    InlineEditorComponent,
    UpdateLimitPopupComponent,
  ]
})
export class RuleModalComponent implements AfterViewInit {
  @ViewChild(InlineEditorComponent) private inlineEditor: InlineEditorComponent;
  @ViewChild('updateLimitButton') private updateLimitButton?: ElementRef<HTMLButtonElement>;

  isUpdateLimitOpen = false;
  saveSubject = new Subject<void>();
  canSave = false;
  hasPendingChanges: boolean;
  enrichCreditsExhausted = false;

  readonly enrichCreditBlockedTooltip = ENRICH_CREDIT_EXHAUSTED_TOOLTIP;

  nameControl: FormControl;
  updateLimitControl: FormControl<number | null>;
  entityLabel: string;
  entityIconClass: string;

  constructor(
    public dialogRef: MatDialogRef<RuleModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IRuleModalData,
    private utilsService: UtilsService,
    private enrichmentCreditService: EnrichmentCreditService
  ) {
    this.hasPendingChanges = this.data.dataRule.viewState.dirty;
    this.nameControl = new FormControl(this.data.dataRule.ruleData.name, Validators.required);
    this.updateLimitControl = new FormControl<number | null>(this.data.dataRule.ruleData.recordUpdateLimit ?? null, Validators.min(1));
    this.entityLabel = this.data.entityMap?.[this.data.selectedEntity] ?? '';
    this.entityIconClass = this.utilsService.renderEntityIcon(
      this.utilsService.parseCamelCaseToHyphenDivided(this.data.selectedEntity ?? '')
    );
    this.loadEnrichmentCredit();
  }

  ngAfterViewInit(): void {
    if (this.isUnsavedRule) {
      this.inlineEditor.onEdit();
    }
  }

  get isUnsavedRule(): boolean {
    return this.utilsService.compareInternalDataRuleIds(this.data.dataRule.ruleData.id, 0);
  }

  get isArchived(): boolean {
    return this.data.dataRule.ruleData.isArchived;
  }

  get hasEnrichAction(): boolean {
    const steps = this.data.dataRule.ruleData.action?.config?.steps;
    return steps?.some((step: any) => step.type === ActionsOperators.ENRICH) ?? false;
  }

  get isEnrichCreditBlocked(): boolean {
    return this.hasEnrichAction && this.enrichCreditsExhausted;
  }

  get playTooltip(): string {
    if (this.hasPendingChanges) {
      return 'Rule needs saving before running';
    }
    if (this.isArchived) {
      return 'Archived rules cannot be run';
    }
    if (this.isEnrichCreditBlocked) {
      return this.enrichCreditBlockedTooltip;
    }
    return 'View results for ' + this.data.dataRule.ruleData.name;
  }

  get updateLimitTooltip(): string {
    if (this.isArchived) {
      return 'Archived Rules cannot be edited';
    }
    const limit = this.updateLimitControl.value;
    return limit ? `Update limit: ${UpdateLimitPopupComponent.formatLimit(limit)} records per run` : 'Update limit: none set';
  }

  toggleUpdateLimit(): void {
    if (this.isArchived) {
      return;
    }
    if (this.isUpdateLimitOpen) {
      this.closeUpdateLimit();
    } else {
      this.isUpdateLimitOpen = true;
    }
  }

  closeUpdateLimit(): void {
    this.isUpdateLimitOpen = false;
    this.updateLimitButton?.nativeElement.focus();
  }

  private loadEnrichmentCredit(): void {
    if (!this.hasEnrichAction) {
      return;
    }
    this.enrichmentCreditService.getCreditBalance().subscribe({
      next: balance => {
        this.enrichCreditsExhausted = balance.balance <= 0;
      }
    });
  }

  onRuleSaved(saved: boolean): void {
    if (saved) {
      this.triggerPreview();
    }
  }

  onCanSaveChange(value: boolean): void {
    this.canSave = value;
  }

  onPendingChanges(value: boolean): void {
    this.hasPendingChanges = value;
  }

  onDuplicateClick(): void {
    if (this.hasPendingChanges) {
      return;
    }
    this.dialogRef.close(RuleModalCloseAction.Duplicate);
  }

  onDeleteClick(): void {
    if (this.isUnsavedRule) {
      this.dialogRef.close();
      return;
    }
    this.dialogRef.close(RuleModalCloseAction.Delete);
  }

  onArchiveClick(): void {
    if (this.isArchived) {
      this.data.onUnarchiveRequested?.();
    } else {
      this.data.onArchiveRequested?.();
    }
  }

  onPreviewClick(): void {
    if (this.hasPendingChanges || this.isUnsavedRule || this.isArchived || this.isEnrichCreditBlocked) {
      return;
    }
    this.triggerPreview();
  }

  onSaveAndPreviewClick(): void {
    if (this.hasPendingChanges || this.isUnsavedRule) {
      this.saveSubject.next();
    } else {
      this.triggerPreview();
    }
  }

  private triggerPreview(): void {
    this.data.currentRuleExamples = { ...this.data.dataRule.ruleData };
  }
}
